import type { Area } from 'react-easy-crop';
import {
  drawVideoCaption,
  ensureCaptionFontsLoaded,
  hasCaptionText,
  type VideoCaption,
} from './videoCaption';

/** Long edge 1920, both sides even — H.264 rejects odd dimensions. 9:16 becomes 1080×1920. */
export function videoOutputSize(aspect: number): { width: number; height: number } {
  const a = aspect > 0 ? aspect : 9 / 16;
  const even = (n: number) => {
    const r = Math.max(2, Math.round(n));
    return r % 2 === 0 ? r : r - 1;
  };
  if (a < 0.98) return { width: even(1920 * a), height: 1920 };
  if (a > 1.02) return { width: 1920, height: even(1920 / a) };
  return { width: 1080, height: 1080 };
}

function coverCrop(videoWidth: number, videoHeight: number, aspect: number): Area {
  const imgAspect = videoWidth / Math.max(1, videoHeight);
  if (imgAspect > aspect) {
    const height = videoHeight;
    const width = height * aspect;
    return { width, height, x: (videoWidth - width) / 2, y: 0 };
  }
  const width = videoWidth;
  const height = width / aspect;
  return { width, height, x: 0, y: (videoHeight - height) / 2 };
}

function clampCrop(area: Area, videoWidth: number, videoHeight: number): Area {
  let { width, height, x, y } = area;
  width = Math.max(1, Math.round(width));
  height = Math.max(1, Math.round(height));
  x = Math.max(0, Math.min(videoWidth - 1, Math.round(x)));
  y = Math.max(0, Math.min(videoHeight - 1, Math.round(y)));
  if (x + width > videoWidth) width = videoWidth - x;
  if (y + height > videoHeight) height = videoHeight - y;
  return { x, y, width: Math.max(1, width), height: Math.max(1, height) };
}

/**
 * Grab a single frame from a video as a JPEG data URL — used as the thumbnail
 * in the composer and as visual context for the AI caption writer.
 */
export async function captureVideoPoster(
  url: string,
  options: { atSeconds?: number; maxEdge?: number; quality?: number } = {},
): Promise<string> {
  const video = await loadVideo(url);
  const maxEdge = options.maxEdge ?? 640;
  const quality = options.quality ?? 0.82;
  try {
    const at = Math.min(
      Math.max(0.1, options.atSeconds ?? 0.5),
      Math.max(0.1, (video.duration || 1) - 0.1),
    );
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('Timed out reading the video')), 8000);
      video.onseeked = () => {
        window.clearTimeout(timer);
        resolve();
      };
      video.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('Could not read the video'));
      };
      video.currentTime = at;
    });
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) throw new Error('Could not read this video’s size');
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    video.pause();
    video.src = '';
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the cover photo'));
    img.src = url;
  });
}

async function seekVideo(video: HTMLVideoElement, atSeconds: number): Promise<void> {
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  const at = Math.min(Math.max(0, atSeconds), duration > 0.05 ? duration - 0.05 : 0);
  if (video.readyState >= 2 && Math.abs(video.currentTime - at) < 0.03) return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Timed out reading the video')), 8000);
    video.onseeked = () => {
      window.clearTimeout(timer);
      resolve();
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('Could not read the video'));
    };
    video.currentTime = at;
  });
}

/**
 * JPEG cover for a video post, sized to the file that will actually be published.
 * A photo is center-cropped to that size. A frame uses the same crop (and burned-in
 * caption) as the video when the video itself is being re-encoded.
 */
export async function renderVideoCoverJpeg(options: {
  aspect: number;
  /** Pixel size of the posted video. Omitted: use the format size, or the video's own size. */
  outputSize?: { width: number; height: number } | null;
  videoUrl?: string | null;
  atSeconds?: number;
  croppedAreaPixels?: Area | null;
  caption?: VideoCaption | null;
  photoUrl?: string | null;
}): Promise<Blob> {
  const caption = hasCaptionText(options.caption) ? options.caption : null;
  if (caption) await ensureCaptionFontsLoaded();

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');

  if (options.photoUrl) {
    const img = await loadImage(options.photoUrl);
    let size = options.outputSize;
    if ((!size?.width || !size?.height) && options.videoUrl) {
      const video = await loadVideo(options.videoUrl);
      size = { width: video.videoWidth, height: video.videoHeight };
      video.pause();
      video.src = '';
    }
    const out = size?.width && size?.height
      ? { width: Math.max(1, Math.round(size.width)), height: Math.max(1, Math.round(size.height)) }
      : videoOutputSize(options.aspect);
    canvas.width = out.width;
    canvas.height = out.height;
    const scale = Math.max(out.width / img.naturalWidth, out.height / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    ctx.drawImage(img, (out.width - dw) / 2, (out.height - dh) / 2, dw, dh);
  } else {
    if (!options.videoUrl) throw new Error('No video to take a cover from');
    const video = await loadVideo(options.videoUrl);
    try {
      await seekVideo(video, options.atSeconds ?? 0);
      const sourceW = video.videoWidth;
      const sourceH = video.videoHeight;
      if (!sourceW || !sourceH) throw new Error('Could not read this video’s size');
      const crop = options.croppedAreaPixels
        ? clampCrop(options.croppedAreaPixels, sourceW, sourceH)
        : { x: 0, y: 0, width: sourceW, height: sourceH };
      const out = options.outputSize
        ? { width: Math.max(1, Math.round(options.outputSize.width)), height: Math.max(1, Math.round(options.outputSize.height)) }
        : options.croppedAreaPixels
          ? videoOutputSize(options.aspect)
          : { width: sourceW, height: sourceH };
      canvas.width = out.width;
      canvas.height = out.height;
      ctx.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, out.width, out.height);
      if (caption) drawVideoCaption(ctx, caption, out.width, out.height);
    } finally {
      video.pause();
      video.src = '';
    }
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b && b.size > 500 ? resolve(b) : reject(new Error('Could not create the cover image'))),
      'image/jpeg',
      0.9,
    );
  });
  return blob;
}

/**
 * Small JPEGs spread through a video, skipping the opening and closing moments.
 * Used so a vision model can pick a cover without watching the whole file.
 */
export async function sampleVideoFrames(
  url: string,
  options: { count?: number; maxEdge?: number } = {},
): Promise<{ time: number; dataUrl: string }[]> {
  const count = Math.max(2, Math.min(6, options.count ?? 5));
  const maxEdge = options.maxEdge ?? 420;
  const video = await loadVideo(url);
  try {
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration < 0.4) return [];
    const start = Math.min(Math.max(0.15, duration * 0.12), duration * 0.4);
    const end = Math.max(start + 0.15, Math.min(duration * 0.88, duration - 0.12));
    const times = Array.from({ length: count }, (_, i) => start + ((end - start) * i) / (count - 1));
    const out: { time: number; dataUrl: string }[] = [];
    for (const time of times) {
      await seekVideo(video, time);
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) continue;
      const scale = Math.min(1, maxEdge / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      out.push({ time, dataUrl: canvas.toDataURL('image/jpeg', 0.72) });
    }
    return out;
  } finally {
    video.pause();
    video.src = '';
  }
}

const MP4_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
];

function mp4Mime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return MP4_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || null;
}

/** Width, height, and length of a video by URL (no decoding beyond the header). */
export async function probeVideo(
  url: string,
): Promise<{ width: number; height: number; duration: number }> {
  const video = await loadVideo(url);
  const result = {
    width: video.videoWidth,
    height: video.videoHeight,
    duration: Number.isFinite(video.duration) ? video.duration : 0,
  };
  video.removeAttribute('src');
  video.load();
  return result;
}

function loadVideo(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;
    const fail = () => reject(new Error('Could not load the video to crop it'));
    video.onloadedmetadata = () => resolve(video);
    video.onerror = fail;
  });
}

/**
 * Re-encode a video to the crop the user framed, with an optional burned-in
 * caption. Meta has no crop-offset field, so the framing has to be in the file.
 * Plays back in real time (the browser encodes while it plays) and keeps the
 * original audio when it can.
 */
export async function bakeCroppedVideo(options: {
  url: string;
  croppedAreaPixels: Area | null | undefined;
  aspect: number;
  caption?: VideoCaption | null;
  onProgress?: (ratio: number) => void;
}): Promise<Blob> {
  const mime = mp4Mime();
  if (!mime) {
    throw new Error('This browser can’t prepare a video for Instagram. Try Chrome or Safari.');
  }
  const caption = hasCaptionText(options.caption) ? options.caption : null;
  if (caption) await ensureCaptionFontsLoaded();
  const video = await loadVideo(options.url);
  const duration = video.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error('Could not read the length of this video');
  }
  const sourceW = video.videoWidth;
  const sourceH = video.videoHeight;
  if (!sourceW || !sourceH) throw new Error('Could not read this video’s size');

  const crop = clampCrop(
    options.croppedAreaPixels || coverCrop(sourceW, sourceH, options.aspect),
    sourceW,
    sourceH,
  );
  const { width: outW, height: outH } = videoOutputSize(options.aspect);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');

  const draw = () => {
    ctx.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, outW, outH);
    if (caption) drawVideoCaption(ctx, caption, outW, outH);
  };
  video.currentTime = 0;
  await new Promise<void>((resolve) => {
    if (video.readyState >= 2) resolve();
    else video.onseeked = () => resolve();
  });
  draw();

  let audioTrack: MediaStreamTrack | null = null;
  let audioCtx: AudioContext | null = null;
  try {
    audioCtx = new AudioContext();
    await Promise.race([
      audioCtx.resume(),
      new Promise((resolve) => window.setTimeout(resolve, 400)),
    ]);
    if (audioCtx.state === 'running') {
      const source = audioCtx.createMediaElementSource(video);
      const dest = audioCtx.createMediaStreamDestination();
      const silent = audioCtx.createGain();
      silent.gain.value = 0;
      source.connect(dest);
      source.connect(silent);
      silent.connect(audioCtx.destination);
      audioTrack = dest.stream.getAudioTracks()[0] || null;
    }
  } catch {
    audioTrack = null;
  }

  const canvasStream = canvas.captureStream(30);
  if (audioTrack) canvasStream.addTrack(audioTrack);

  const blob = await new Promise<Blob>((resolve, reject) => {
    let recorder: MediaRecorder;
    const recorderOptions = { mimeType: mime, videoBitsPerSecond: 8_000_000 };
    try {
      recorder = new MediaRecorder(canvasStream, recorderOptions);
    } catch {
      if (audioTrack) canvasStream.removeTrack(audioTrack);
      try {
        recorder = new MediaRecorder(canvasStream, recorderOptions);
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Could not start video export'));
        return;
      }
    }
    const chunks: Blob[] = [];
    let settled = false;
    const succeed = (out: Blob) => {
      if (settled) return;
      settled = true;
      resolve(out);
    };
    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      if (recorder.state === 'recording') recorder.stop();
      reject(err);
    };
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onerror = () => fail(new Error('Video export failed'));
    recorder.onstop = () => {
      const out = new Blob(chunks, { type: 'video/mp4' });
      if (out.size < 1000) fail(new Error('Video export produced an empty file'));
      else succeed(out);
    };

    let frameId = 0;
    const pump = () => {
      if (video.ended || video.paused) return;
      draw();
      options.onProgress?.(Math.min(0.99, video.currentTime / duration));
      if ('requestVideoFrameCallback' in video) {
        frameId = video.requestVideoFrameCallback(() => pump());
      } else {
        frameId = requestAnimationFrame(pump);
      }
    };

    const finish = () => {
      if ('cancelVideoFrameCallback' in video && frameId) {
        video.cancelVideoFrameCallback(frameId);
      }
      window.setTimeout(() => {
        if (recorder.state === 'recording') recorder.stop();
      }, 200);
    };
    video.addEventListener('ended', finish, { once: true });

    recorder.start();
    video.play().then(pump).catch((err) => {
      fail(err instanceof Error ? err : new Error('Could not play the video to crop it'));
    });

    window.setTimeout(() => {
      if (recorder.state === 'recording') finish();
    }, duration * 1000 + 8000);
  }).finally(() => {
    video.pause();
    video.src = '';
    void audioCtx?.close();
  });

  options.onProgress?.(1);
  return blob;
}
